import { Link } from "react-router-dom";
import { ArrowRight, Factory, Calculator, FileText, Users, ScrollText, Building2, ShoppingCart, Wrench, User, Briefcase, BadgeCheck, Clock, Languages, MessageCircle } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import PricingCalculator from "@/components/PricingCalculator";
import CTABanner from "@/components/CTABanner";
import { usePageMeta, useScrollReveal } from "@/lib/site";

const services = [
  {
    icon: FileText,
    title: "Vedení účetnictví",
    desc: "Plné podvojné účetnictví, DPH, kontrolní hlášení, závěrky a daně z příjmů — vše v termínech a bez stresu.",
  },
  {
    icon: ScrollText,
    title: "Daňová evidence",
    desc: "Pro OSVČ a menší subjekty. Přehled nad příjmy a výdaji, roční zúčtování, klid v hlavě.",
  },
  {
    icon: Users,
    title: "Zpracování mezd",
    desc: "Mzdy, srážky, ELDP, ČSSZ, zdravotní pojišťovny — komunikujeme za vás se všemi institucemi.",
  },
  {
    icon: Calculator,
    title: "Daňové poradenství",
    desc: "Optimalizace, předinvestiční konzultace, podpora při auditu, mezinárodní zdanění dle potřeby.",
  },
];

const trust = [
  { icon: Briefcase, label: "Pro firmy i živnostníky" },
  { icon: BadgeCheck, label: "Osobní přístup, ne callcentrum" },
  { icon: Clock, label: "Termíny dodržíme" },
  { icon: Languages, label: "Komunikujeme bez účetní hatmatilky" },
];

const audience = [
  { icon: Building2, label: "Právnické osoby" },
  { icon: User, label: "OSVČ a živnostníci" },
  { icon: ShoppingCart, label: "E-shopy a obchod" },
  { icon: Wrench, label: "Služby a řemesla" },
  { icon: Factory, label: "Výroba a stavebnictví" },
];

export default function Home() {
  usePageMeta(
    "Účetnictví pro firmy a živnostníky v Brně — G&G accounting",
    "Účetní partner pro malé a střední s.r.o. a OSVČ v Brně a okolí. Osobní přístup, jasná čísla, kalkulačka ceny online."
  );
  useScrollReveal();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-magenta/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 reveal">
              <Eyebrow>Účetnictví pro firmy a živnostníky</Eyebrow>
              <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] tracking-tighter font-medium mt-6 leading-[0.98]">
                Účetnictví, kterému rozumíme.
                <br />
                <span className="text-magenta">A vy nám důvěřujete.</span>
              </h1>
              <p className="mt-8 text-lg md:text-xl text-muted2 leading-relaxed max-w-2xl">
                Jsme účetní partner pro malé a střední s.r.o. a OSVČ v Brně a okolí, napříč obory.
                Mluvíme srozumitelně — řečí firmy i daňových zákonů. Vy se věnujte svému podnikání,
                čísla nechte na nás.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white px-8 py-4 rounded-full font-medium transition-colors"
                >
                  Nezávazná poptávka <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#sluzby"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-ink/20 hover:border-magenta hover:text-magenta text-ink px-8 py-4 rounded-full font-medium transition-colors"
                >
                  Zjistit více
                </a>
              </div>
            </div>
            <div className="lg:col-span-4 reveal">
              <div className="relative aspect-[4/5] w-full bg-ink2 rounded-md overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/37294826/pexels-photo-37294826.jpeg"
                  alt="G&G accounting — moderní pracovní prostředí účetní firmy v Brně"
                  loading="eager"
                  fetchpriority="high"
                  width="720"
                  height="900"
                  className="absolute inset-0 w-full h-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink2/70 via-ink2/10 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="text-xs uppercase tracking-[0.22em] text-magenta font-semibold">Pro koho jsme tady</div>
                  <div className="font-display text-xl md:text-2xl mt-2 leading-tight">Firmy, OSVČ a živnostníci</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-line bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 gap-8 md:flex md:flex-nowrap md:items-center md:justify-between md:gap-6">
          {trust.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} className="flex items-center gap-3 reveal md:whitespace-nowrap" style={{ transitionDelay: `${i * 60}ms` }}>
                <Icon className="w-5 h-5 text-magenta shrink-0" />
                <span className="text-sm md:text-base font-medium text-ink leading-snug">{t.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section id="sluzby" className="px-6 md:px-12 py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16 reveal">
            <Eyebrow>Co pro vás uděláme</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
              Kompletní účetní servis.
              <br />
              <span className="text-muted2">Bez výmluv, bez chaosu.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-md overflow-hidden">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="bg-white p-8 md:p-10 group reveal hover:bg-cream transition-colors flex flex-col h-full"
                  style={{ transitionDelay: `${i * 80}ms` }}
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-md bg-magenta/10 flex items-center justify-center mb-6 group-hover:bg-magenta transition-colors">
                    <Icon className="w-5 h-5 text-magenta group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-display text-xl font-medium mb-3">{s.title}</h3>
                  <p className="text-sm text-muted2 leading-relaxed mb-6">{s.desc}</p>
                  <Link
                    to="/sluzby"
                    className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-ink group-hover:text-magenta transition-colors"
                    data-testid={`service-link-${i}`}
                  >
                    Více <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="px-6 md:px-12 py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <Eyebrow>Proč G&amp;G</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
              Známe svět <span className="text-magenta">podnikání</span> zevnitř.
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg text-muted2 leading-relaxed">
              <p>
                Každá firma je jiná. E-shop má jiné starosti než řemeslník, restaurace jiné než
                výrobce. Účetnictví musí zachytit přesně to, na čem váš byznys reálně stojí — jinak
                ztrácíte přehled tam, kde si to nemůžete dovolit.
              </p>
              <p>
                Pracujeme dlouhodobě s firmami i živnostníky napříč obory — od služeb a obchodu přes
                výrobu až po stavebnictví. Známe specifika různých provozů a víme, kde se skrývá
                riziko a kde naopak prostor pro úsporu.
              </p>
              <p>
                Komunikujeme srozumitelně. Žádná účetní hatmatilka — jen jasná čísla, na základě
                kterých se rozhodujete s jistotou.
              </p>
            </div>
            <div className="mt-10">
              <Link to="/o-nas" className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-magenta transition-colors" data-testid="why-us-link">
                Více o nás <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[4/5] bg-ink2 rounded-md overflow-hidden">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Brno_Astronomical_Clock.jpg/960px-Brno_Astronomical_Clock.jpg" alt="Detail pracovního prostředí účetní firmy" width="720" height="900" className="w-full h-full object-cover object-[center_30%]" loading="lazy" />
              </div>
              <div className="aspect-[4/5] bg-ink2 rounded-md overflow-hidden mt-12">
                <img src="https://images.pexels.com/photos/18586561/pexels-photo-18586561.jpeg" alt="Abstraktní geometrický motiv" width="720" height="900" className="w-full h-full object-cover object-bottom scale-150 origin-bottom -translate-y-[-7%]" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE STRIP */}
      <section className="bg-ink2 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="max-w-3xl mb-12 reveal">
            <Eyebrow>Pro koho jsme tady</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05] text-white">
              Pro firmy a živnostníky <span className="text-magenta">napříč obory</span>.
            </h2>
            <p className="mt-6 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl">
              Pomáháme malým a středním s.r.o., OSVČ i živnostníkům v Brně a okolí — od služeb přes
              obchod až po výrobu. Každé firmě se věnujeme stejně pečlivě, ať vede sklad, kavárnu,
              nebo dílnu.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
            {audience.map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} className="bg-ink2 p-6 md:p-8 flex flex-col items-center justify-center gap-3 text-center reveal" style={{ transitionDelay: `${i * 50}ms` }}>
                  <Icon className="w-6 h-6 text-magenta" />
                  <span className="text-sm font-medium text-white/85">{a.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <PricingCalculator id="kalkulacka" />

      {/* CTA */}
      <CTABanner
        eyebrow="Pojďme se nezávazně bavit"
        title="O vašem účetnictví. V klidu, bez tlaku."
        text="Stačí krátká schůzka — řekneme si, co potřebujete, a navrhneme řešení šité na míru vaší firmě."
        primaryHref="/kontakt"
        primaryLabel="Napsat nám"
      />
    </div>
  );
}
